import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Settings2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─────────── Fahrwerk Konfigurator ─────────── */
function FahrwerkKonfigurator({ hideFahrertyp = false }) {
  const [marken, setMarken] = useState([]);
  const [modelle, setModelle] = useState([]);
  const [typen, setTypen] = useState([]);
  const [baujahre, setBaujahre] = useState([]);

  const [marke, setMarke] = useState('');
  const [modell, setModell] = useState('');
  const [typ, setTyp] = useState('');
  const [baujahr, setBaujahr] = useState('');
  const [fahrertyp, setFahrertyp] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/api/shop/fahrwerk/konfigurator/marken`)
      .then(r => r.json()).then(d => setMarken(d.marken || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!marke) { setModelle([]); return; }
    fetch(`${API}/api/shop/fahrwerk/konfigurator/modelle?marke=${encodeURIComponent(marke)}`)
      .then(r => r.json()).then(d => setModelle(d.modelle || [])).catch(() => {});
  }, [marke]);

  useEffect(() => {
    if (!modell) { setTypen([]); return; }
    fetch(`${API}/api/shop/fahrwerk/konfigurator/typen?marke=${encodeURIComponent(marke)}&modell=${encodeURIComponent(modell)}`)
      .then(r => r.json()).then(d => setTypen(d.typen || [])).catch(() => {});
  }, [marke, modell]);

  useEffect(() => {
    if (!typ) { setBaujahre([]); return; }
    fetch(`${API}/api/shop/fahrwerk/konfigurator/baujahre?marke=${encodeURIComponent(marke)}&modell=${encodeURIComponent(modell)}&typ=${encodeURIComponent(typ)}`)
      .then(r => r.json()).then(d => setBaujahre(d.baujahre || [])).catch(() => {});
  }, [marke, modell, typ]);

  const handleGo = () => {
    const params = new URLSearchParams();
    if (marke) params.set('marke', marke);
    if (modell) params.set('modell', modell);
    if (typ) params.set('typ', typ);
    if (baujahr) params.set('baujahr', baujahr);
    if (fahrertyp) params.set('fahrertyp', fahrertyp);
    navigate(`/fahrwerke?${params.toString()}`);
  };

  const FAHRERTYPEN = [
    { id: 'allrounder', label: 'Alleskönner', desc: 'Komfort & Optik vereint' },
    { id: 'komfort', label: 'Komfort', desc: 'Alltagstauglich tiefer' },
    { id: 'tief', label: 'Tief', desc: 'Maximale Tieferlegung' },
    { id: 'race', label: 'Race', desc: 'Trackday-Performance' },
  ];

  return (
    <div className="konfigurator" data-testid="fahrwerk-konfigurator">
      <h2 className="konfigurator-title">Fahrwerk-Konfigurator</h2>

      {/* Fahrertyp badges - hidden when list page has its own section */}
      {!hideFahrertyp && (
        <div className="fahrertyp-row" data-testid="fahrertyp-filter">
          {FAHRERTYPEN.map(ft => (
            <button
              key={ft.id}
              className={`fahrertyp-badge ${fahrertyp === ft.id ? 'active' : ''}`}
              onClick={() => setFahrertyp(fahrertyp === ft.id ? '' : ft.id)}
              data-testid={`fahrertyp-${ft.id}`}
            >
              <span className="fahrertyp-label">{ft.label}</span>
              <span className="fahrertyp-desc">{ft.desc}</span>
            </button>
          ))}
        </div>
      )}

      <div className="konfigurator-fields">
        <div className="konfigurator-field">
          <label>Marke</label>
          <select value={marke} onChange={e => { setMarke(e.target.value); setModell(''); setTyp(''); setBaujahr(''); }} data-testid="fw-konfig-marke">
            <option value="">Marke wählen...</option>
            {marken.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="konfigurator-field">
          <label>Modell</label>
          <select value={modell} onChange={e => { setModell(e.target.value); setTyp(''); setBaujahr(''); }} disabled={!marke} data-testid="fw-konfig-modell">
            <option value="">{marke ? 'Modell wählen...' : 'Erst Marke wählen...'}</option>
            {modelle.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="konfigurator-field">
          <label>Typ / Motorisierung</label>
          <select value={typ} onChange={e => { setTyp(e.target.value); setBaujahr(''); }} disabled={!modell} data-testid="fw-konfig-typ">
            <option value="">{modell ? 'Typ wählen...' : 'Erst Modell wählen...'}</option>
            {typen.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="konfigurator-field">
          <label>Baujahr</label>
          <select value={baujahr} onChange={e => setBaujahr(e.target.value)} disabled={!typ} data-testid="fw-konfig-baujahr">
            <option value="">{typ ? 'Baujahr wählen...' : 'Erst Typ wählen...'}</option>
            {baujahre.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
      </div>

      <button className="konfigurator-btn" disabled={!marke} onClick={handleGo} data-testid="fw-konfig-go-btn">
        Fahrwerke anzeigen
      </button>
    </div>
  );
}

/* ─────────── Standard Konfigurator (Performance Parts) ─────────── */
function PerformanceKonfigurator() {
  const [tree, setTree] = useState([]);
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [levels, setLevels] = useState([]); // selected alias per depth below model
  const [catFilters, setCatFilters] = useState({ engines: [], powers_kw: [], enabled: false });
  const [engine, setEngine] = useState('');
  const [powerKw, setPowerKw] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/api/shop/konfigurator/tree`)
      .then(r => r.json()).then(d => setTree(d.brands || [])).catch(() => {});
  }, []);

  const selectedBrand = useMemo(() => tree.find(b => b.alias === brand), [tree, brand]);
  const models = selectedBrand?.models || [];
  const selectedModel = useMemo(() => models.find(m => m.alias === model), [models, model]);

  // Build a dynamic chain of dropdowns for arbitrary category depth below the model
  const levelData = useMemo(() => {
    const out = [];
    let opts = selectedModel?.children || [];
    let depth = 0;
    while (opts && opts.length > 0) {
      const selected = levels[depth] || '';
      const hasDeeper = opts.some(o => o.children && o.children.length > 0);
      out.push({ depth, options: opts, selected, hasDeeper });
      const node = opts.find(o => o.alias === selected);
      if (!node || !(node.children && node.children.length > 0)) break;
      opts = node.children;
      depth++;
    }
    return out;
  }, [selectedModel, levels]);

  const levelLabel = (depth, hasDeeper) => {
    if (depth === 0) return hasDeeper ? 'Baureihe / Serie' : 'Kategorie / Zubehör';
    if (depth === 1) return hasDeeper ? 'Kategorie' : 'Kategorie / Zubehör';
    return 'Unterkategorie';
  };

  const selectLevel = (depth, value) => {
    setLevels(prev => [...prev.slice(0, depth), value]); // truncate deeper selections
  };

  // Current category path from brand/model/selected levels (contiguous, up to last selected)
  const chosenLevels = useMemo(() => {
    const arr = [];
    for (const ld of levelData) {
      if (!ld.selected) break;
      arr.push(ld.selected);
    }
    return arr;
  }, [levelData]);

  const currentCatPath = useMemo(() => {
    if (!brand) return '';
    let p = brand;
    if (model) p += `/${model}`;
    for (const a of chosenLevels) p += `/${a}`;
    return p;
  }, [brand, model, chosenLevels]);

  // Only load filters once the deepest selected level is a leaf (no further children)
  const deepestIsLeaf = useMemo(() => {
    if (chosenLevels.length === 0) return false;
    const last = levelData[chosenLevels.length - 1];
    return last && !last.hasDeeper;
  }, [chosenLevels, levelData]);

  useEffect(() => {
    if (!deepestIsLeaf || !currentCatPath) {
      setCatFilters({ engines: [], powers_kw: [], enabled: false });
      setEngine(''); setPowerKw('');
      return;
    }
    fetch(`${API}/api/shop/category-filters?category=${encodeURIComponent(currentCatPath)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && d.enabled) {
          setCatFilters({ engines: d.engines || [], powers_kw: d.powers_kw || [], enabled: true });
        } else {
          setCatFilters({ engines: [], powers_kw: [], enabled: false });
        }
        setEngine(''); setPowerKw('');
      })
      .catch(() => setCatFilters({ engines: [], powers_kw: [], enabled: false }));
  }, [deepestIsLeaf, currentCatPath]);

  const handleGo = () => {
    if (!currentCatPath) return;
    const params = new URLSearchParams({ category: currentCatPath });
    if (engine) params.set('engine', engine);
    if (powerKw) params.set('power_kw', powerKw);
    navigate(`/shop?${params.toString()}`);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="product-grid"], [data-testid="featured-row"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 600);
  };

  return (
    <div className="konfigurator" data-testid="konfigurator">
      <h2 className="konfigurator-title">Fahrzeug-Konfigurator</h2>
      <div className="konfigurator-fields">
        <div className="konfigurator-field">
          <label>Marke</label>
          <select value={brand} onChange={e => { setBrand(e.target.value); setModel(''); setLevels([]); }} data-testid="konfig-brand">
            <option value="">Marke wählen...</option>
            {tree.map(b => <option key={b.alias} value={b.alias}>{b.name}</option>)}
          </select>
        </div>
        <div className="konfigurator-field">
          <label>Modell</label>
          <select value={model} onChange={e => { setModel(e.target.value); setLevels([]); }} disabled={!brand} data-testid="konfig-model">
            <option value="">{brand ? 'Modell wählen...' : 'Bitte zuerst Marke wählen...'}</option>
            {models.map(m => <option key={m.alias} value={m.alias}>{m.name}</option>)}
          </select>
        </div>
        {levelData.map((ld) => (
          <div className="konfigurator-field" key={ld.depth}>
            <label>{levelLabel(ld.depth, ld.hasDeeper)}</label>
            <select
              value={ld.selected}
              onChange={e => selectLevel(ld.depth, e.target.value)}
              disabled={ld.depth > 0 && !levels[ld.depth - 1]}
              data-testid={`konfig-level-${ld.depth}`}
            >
              <option value="">{ld.hasDeeper ? 'Auswahl…' : 'Kategorie wählen...'}</option>
              {ld.options.map(o => <option key={o.alias} value={o.alias}>{o.name}</option>)}
            </select>
          </div>
        ))}
        {catFilters.enabled && catFilters.engines.length > 0 && (
          <div className="konfigurator-field" data-testid="konfig-engine-wrap">
            <label>Motor <span style={{ opacity: 0.5, fontWeight: 400 }}>(optional)</span></label>
            <select value={engine} onChange={e => setEngine(e.target.value)} data-testid="konfig-engine">
              <option value="">Alle Motoren</option>
              {catFilters.engines.map(e => (
                <option key={e.value} value={e.value}>{e.label} ({e.count})</option>
              ))}
            </select>
          </div>
        )}
        {catFilters.enabled && catFilters.powers_kw.length > 0 && (
          <div className="konfigurator-field" data-testid="konfig-power-wrap">
            <label>Leistung <span style={{ opacity: 0.5, fontWeight: 400 }}>(optional)</span></label>
            <select value={powerKw} onChange={e => setPowerKw(e.target.value)} data-testid="konfig-power">
              <option value="">Alle Leistungen</option>
              {catFilters.powers_kw.map(p => (
                <option key={p.value} value={p.value}>{p.label} ({p.count})</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <button className="konfigurator-btn" disabled={!model} onClick={handleGo} data-testid="konfig-go-btn">
        Zur Kategorie im Shop
      </button>
    </div>
  );
}

/* ─────────── Dual Konfigurator with Tab Switch ─────────── */
const DualKonfigurator = ({ compact = false, initialTab = 'performance', onTabChange, hideFahrertyp = false }) => {
  const [activeTab, setActiveTab] = useState(initialTab);

  const switchTab = (tab) => {
    setActiveTab(tab);
    if (onTabChange) onTabChange(tab);
  };

  return (
    <div className={`dual-konfigurator ${compact ? 'konfigurator-compact' : ''}`} data-testid="dual-konfigurator">
      <div className="konfig-tabs" data-testid="konfig-tabs">
        <button
          className={`konfig-tab ${activeTab === 'performance' ? 'active' : ''}`}
          onClick={() => switchTab('performance')}
          data-testid="konfig-tab-performance"
        >
          <Wrench size={16} />
          <span>Performance Parts</span>
        </button>
        <button
          className={`konfig-tab ${activeTab === 'fahrwerk' ? 'active' : ''}`}
          onClick={() => switchTab('fahrwerk')}
          data-testid="konfig-tab-fahrwerk"
        >
          <Settings2 size={16} />
          <span>Fahrwerktechnik</span>
        </button>
      </div>
      {activeTab === 'performance' ? <PerformanceKonfigurator /> : <FahrwerkKonfigurator hideFahrertyp={hideFahrertyp} />}
    </div>
  );
};

export default DualKonfigurator;
export { PerformanceKonfigurator, FahrwerkKonfigurator };
